import { Grid, Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { ApiConnectionFirmMapping, ApiConnectionUserMapping } from '@tassat/fred-extended-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import ApiConnectionFirmUsersGrid from 'Containers/admin/pages/api/apiFirmView/usersGrid';
import Worker from 'Lib/worker/PrimusWorker';

interface IApiConnectionFirmUsersProps extends WithStyles<typeof muiStyles> {
    apiConnectionFirm: ApiConnectionFirmMapping;
}

const muiStyles = () => ({
    root: {
        flexGrow: 1,
        backgroundColor: '#FFF',
        marginTop: '2.5rem',
        padding: '1rem 1.5rem'
    }
});

export function ApiConnectionFirmUsers (props: IApiConnectionFirmUsersProps) {
    const {
        classes,
        apiConnectionFirm
    } = props;

    const [ ,setSelectedApiUser ] = React.useState<ApiConnectionUserMapping>(null);
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const deleteApiUser = async (apiConnectionUser: ApiConnectionUserMapping) => {
        const id = apiConnectionUser.apiUser.APIConnectionUserId;
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        try {
            const response = await Worker.getInstance()
                .deleteAPIConnectionUser(id);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Deleted Connection for ${user.Email}`,
                        successBody: `Successfully deleted the connection for ${user.Email} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Delete Connection for ${user.Email}`,
                        failureBody: `Could not delete the connection for ${user.Email} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Delete Connection for ${user.Email}`,
                    failureBody: `Something went wrong deleting the connection for ${user.Email} to ${apiConnection.APIConnectionName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const deleteApiUserValidation = (apiConnectionUser: ApiConnectionUserMapping): boolean => {
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        setSelectedApiUser(apiConnectionUser);
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnectionUser,
                actionType: ActionTypes.delete,
                confirmTitle: `Confirm Deleting Connection for ${user.Email}`,
                confirmBody: `Please confirm deleting the connection for ${user.Email} to ${apiConnection.APIConnectionName}`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockApiUserValidation = (apiConnectionUser: ApiConnectionUserMapping): boolean => {
        const apiUser = apiConnectionUser.apiUser;
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        setSelectedApiUser(apiConnectionUser);
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: apiConnectionUser,
                actionType: apiUser.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${apiUser.IsLocked ? 'Unlocking' : 'Locking'} Connection for ${user.Email}`,
                confirmBody: `Please confirm ${apiUser.IsLocked ? 'unlocking' : 'locking'} the connection for ${user.Email} to ${apiConnection.APIConnectionName}`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockApiUser = async (apiConnectionUser: ApiConnectionUserMapping) => {
        const apiUser = apiConnectionUser.apiUser;
        const user = apiConnectionUser.user;
        const apiConnection = apiConnectionUser.apiConnection;

        const sharedUpdates = {
            selectedItem: null,
            alertModalOpen: true,
            confirmModalOpen: false
        };

        try {
            const response = await Worker.getInstance()
                .updateAPIConnectionUser({
                    APIConnectionUserId: apiUser.APIConnectionUserId,
                    IsLocked: !apiUser.IsLocked
                });

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${apiUser.IsLocked ? 'Unlocked' : 'Locked'} Connection for ${user.Email}`,
                        successBody: `Successfully ${apiUser.IsLocked ? 'unlocked' : 'locked'} the connection for ${user.Email} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${apiUser.IsLocked ? 'Unlock' : 'Lock'} Connection for ${user.Email}`,
                        failureBody: `Could not ${apiUser.IsLocked ? 'unlock' : 'lock'} the connection for ${user.Email} to ${apiConnection.APIConnectionName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${apiUser.IsLocked ? 'Unlock' : 'Lock'} Connection for ${user.Email}`,
                    failureBody: `Something went wrong ${apiUser.IsLocked ? 'unlocking' : 'locking'} the connection for ${user.Email} to ${apiConnection.APIConnectionName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                setSelectedItem: setSelectedApiUser,
                preActionValidationAndConfig: {
                    [ActionTypes.delete]: deleteApiUserValidation,
                    [ActionTypes.lock]: lockApiUserValidation,
                    [ActionTypes.unlock]: lockApiUserValidation
                },
                onConfirm: {
                    [ActionTypes.delete]: deleteApiUser,
                    [ActionTypes.lock]: lockApiUser,
                    [ActionTypes.unlock]: lockApiUser
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Typography variant='h6'>API Enabled Users</Typography>
                <ApiConnectionFirmUsersGrid
                    apiConnectionFirmId={apiConnectionFirm.apiFirm.APIConnectionFirmId}
                />
                <ConfirmationWorkflowConsumer />
            </Grid>
        </ConfirmationWorkflowContext.Provider>
    );
}

export default withStyles(muiStyles)(ApiConnectionFirmUsers);